import { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/Card';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { Plus, Trash2, Tag, ShieldCheck, Globe2, Loader2, AlertCircle } from 'lucide-react';
import breederService from '../../services/breeder';
import {
  SYNONYM_TYPE_OPTIONS, RELEASE_STATE_OPTIONS,
  type CultivarSynonym, type SynonymType,
  type ReleaseState, type CultivarRegionRelease,
} from '../../types/breeder';

interface BreederControlsProps {
  cultivarId: number;
  isBreederOwner: boolean;
  initialReleaseState?: ReleaseState | null;
  initialPrimaryDisplaySynonymId?: number | null;
  legacyVisibilityGrace?: boolean;
}

const REGION_RE = /^[A-Z]{2}-[A-Z0-9]{1,3}$/;

function BreederControls({
  cultivarId,
  isBreederOwner,
  initialReleaseState,
  initialPrimaryDisplaySynonymId,
  legacyVisibilityGrace,
}: BreederControlsProps) {
  const [synonyms, setSynonyms] = useState<CultivarSynonym[]>([]);
  const [synonymsLoading, setSynonymsLoading] = useState(true);
  const [synonymError, setSynonymError] = useState<string | null>(null);

  const [newSynonym, setNewSynonym] = useState('');
  const [newSynonymType, setNewSynonymType] = useState<SynonymType>('trial_code');
  const [newSynonymLang, setNewSynonymLang] = useState('');
  const [addingSynonym, setAddingSynonym] = useState(false);

  const [releaseState, setReleaseState] = useState<ReleaseState>(initialReleaseState || 'breeder_only');
  const [primarySynonymId, setPrimarySynonymId] = useState<number | null>(initialPrimaryDisplaySynonymId ?? null);
  const [savingReleaseState, setSavingReleaseState] = useState(false);
  const [releaseStateError, setReleaseStateError] = useState<string | null>(null);
  const [releaseStateSaved, setReleaseStateSaved] = useState(false);

  const [regionReleases, setRegionReleases] = useState<CultivarRegionRelease[]>([]);
  const [regionReleasesLoading, setRegionReleasesLoading] = useState(true);
  const [newRegionCode, setNewRegionCode] = useState('');
  const [regionError, setRegionError] = useState<string | null>(null);
  const [addingRegion, setAddingRegion] = useState(false);

  useEffect(() => {
    void loadSynonyms();
    void loadRegionReleases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cultivarId]);

  const loadSynonyms = async () => {
    setSynonymsLoading(true);
    try {
      const res = await breederService.listSynonyms(cultivarId);
      if (res.success && res.data) setSynonyms(res.data);
    } catch {
      setSynonymError('Failed to load synonyms');
    } finally {
      setSynonymsLoading(false);
    }
  };

  const loadRegionReleases = async () => {
    setRegionReleasesLoading(true);
    try {
      const res = await breederService.listRegionReleases(cultivarId);
      if (res.success && res.data) setRegionReleases(res.data);
    } finally {
      setRegionReleasesLoading(false);
    }
  };

  const handleAddSynonym = async () => {
    if (!newSynonym.trim()) return;
    setAddingSynonym(true);
    setSynonymError(null);
    try {
      const res = await breederService.createSynonym(cultivarId, {
        synonym: newSynonym.trim(),
        type: newSynonymType,
        language_code: newSynonymLang || null,
        valid_from: null,
        valid_until: null,
        notes: null,
      });
      if (res.success && res.data) {
        setSynonyms(prev => [...prev, res.data!]);
        setNewSynonym('');
        setNewSynonymLang('');
      } else {
        setSynonymError(res.message || 'Failed to add synonym');
      }
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message
        || 'Failed to add synonym (it may already be in use)';
      setSynonymError(msg);
    } finally {
      setAddingSynonym(false);
    }
  };

  const handleDeleteSynonym = async (id: number) => {
    setSynonymError(null);
    try {
      const res = await breederService.deleteSynonym(cultivarId, id);
      if (res.success) {
        setSynonyms(prev => prev.filter(s => s.id !== id));
        if (primarySynonymId === id) setPrimarySynonymId(null);
      }
    } catch {
      setSynonymError('Failed to delete synonym');
    }
  };

  const handleSaveReleaseState = async () => {
    setSavingReleaseState(true);
    setReleaseStateError(null);
    setReleaseStateSaved(false);
    try {
      const res = await breederService.setReleaseState(cultivarId, releaseState, primarySynonymId);
      if (res.success) {
        setReleaseStateSaved(true);
        setTimeout(() => setReleaseStateSaved(false), 2500);
      } else {
        setReleaseStateError(res.message || 'Failed to save release state');
      }
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message
        || 'Failed to save release state';
      setReleaseStateError(msg);
    } finally {
      setSavingReleaseState(false);
    }
  };

  const handleAddRegion = async () => {
    setRegionError(null);
    const code = newRegionCode.trim().toUpperCase();
    if (!REGION_RE.test(code)) {
      setRegionError('Region code must look like AU-VIC, US-CA, NZ-AKL');
      return;
    }
    if (regionReleases.some(r => r.region_code === code)) {
      setRegionError('Region already listed below');
      return;
    }
    setAddingRegion(true);
    try {
      const res = await breederService.upsertRegionRelease(cultivarId, code, {
        released_to_growers: false,
        public_synonym_id: null,
        notes: null,
      });
      if (res.success && res.data) {
        setRegionReleases(prev => [...prev, res.data!]);
        setNewRegionCode('');
      } else {
        setRegionError(res.message || 'Failed to add region');
      }
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message
        || 'Failed to add region';
      setRegionError(msg);
    } finally {
      setAddingRegion(false);
    }
  };

  const handleToggleRegionReleased = async (release: CultivarRegionRelease) => {
    try {
      const res = await breederService.upsertRegionRelease(cultivarId, release.region_code, {
        released_to_growers: !release.released_to_growers,
        public_synonym_id: release.public_synonym_id,
        notes: release.notes,
      });
      if (res.success && res.data) {
        setRegionReleases(prev => prev.map(r => r.region_code === release.region_code ? res.data! : r));
      }
    } catch { /* no-op */ }
  };

  const handleSetRegionPublicSynonym = async (release: CultivarRegionRelease, synonymId: number | null) => {
    try {
      const res = await breederService.upsertRegionRelease(cultivarId, release.region_code, {
        released_to_growers: release.released_to_growers,
        public_synonym_id: synonymId,
        notes: release.notes,
      });
      if (res.success && res.data) {
        setRegionReleases(prev => prev.map(r => r.region_code === release.region_code ? res.data! : r));
      }
    } catch { /* no-op */ }
  };

  return (
    <div className="mt-8 space-y-6">
      {legacyVisibilityGrace && isBreederOwner && (
        <div className="bg-warning-50 border border-warning-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-warning-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-warning-800 font-medium">Pending owner review</p>
            <p className="text-warning-700 text-sm">
              This cultivar is on a legacy visibility grace period. Set its release state below to lock visibility down to your IP rules — that will clear the grace period.
            </p>
          </div>
        </div>
      )}

      {/* ---- Synonyms ---- */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Tag className="h-5 w-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-neutral-900">Synonyms</h2>
          </div>
          <p className="text-sm text-neutral-600 mb-4">
            Trial codes, trade names, old names, translations. Use these to refer to the cultivar while keeping the registered name private (e.g. during PBR application).
          </p>

          {synonymsLoading ? (
            <div className="flex items-center gap-2 text-neutral-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading synonyms…</div>
          ) : (
            <>
              {synonyms.length > 0 ? (
                <ul className="space-y-2 mb-4">
                  {synonyms.map(s => (
                    <li key={s.id} className="flex items-center justify-between border border-neutral-200 rounded-md px-3 py-2">
                      <div className="flex items-center gap-3">
                        <span className="font-medium text-neutral-900">{s.synonym}</span>
                        <span className="text-xs uppercase tracking-wide text-neutral-500">{s.type.replace('_', ' ')}</span>
                        {s.language_code && <span className="text-xs text-neutral-400">{s.language_code}</span>}
                      </div>
                      {isBreederOwner && (
                        <Button variant="outline" size="sm" onClick={() => handleDeleteSynonym(s.id)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-neutral-500 mb-4">No synonyms yet.</p>
              )}

              {isBreederOwner && (
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px_120px_auto] gap-2 items-end">
                  <Input
                    label="Add synonym"
                    value={newSynonym}
                    onChange={e => setNewSynonym(e.target.value)}
                    placeholder="e.g. ZGR-NEC-2024-07"
                  />
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Type</label>
                    <select
                      className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
                      value={newSynonymType}
                      onChange={e => setNewSynonymType(e.target.value as SynonymType)}
                    >
                      {SYNONYM_TYPE_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <Input
                    label="Lang"
                    value={newSynonymLang}
                    onChange={e => setNewSynonymLang(e.target.value)}
                    placeholder="en"
                  />
                  <Button onClick={handleAddSynonym} disabled={addingSynonym || !newSynonym.trim()} variant="primary">
                    {addingSynonym ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  </Button>
                </div>
              )}

              {synonymError && (
                <p className="mt-3 text-sm text-error-700">{synonymError}</p>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* ---- Release state ---- */}
      {isBreederOwner && (
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <ShieldCheck className="h-5 w-5 text-primary-600" />
              <h2 className="text-lg font-semibold text-neutral-900">Release state</h2>
            </div>
            <p className="text-sm text-neutral-600 mb-4">
              Controls who can see this cultivar at the global level. Region licensors can further restrict per-region grower release below.
            </p>

            <div className="space-y-2 mb-4">
              {RELEASE_STATE_OPTIONS.map(opt => (
                <label key={opt.value} className="flex items-start gap-3 p-3 border border-neutral-200 rounded-md cursor-pointer hover:bg-neutral-50">
                  <input
                    type="radio"
                    name="release_state"
                    value={opt.value}
                    checked={releaseState === opt.value}
                    onChange={() => setReleaseState(opt.value)}
                    className="mt-1"
                  />
                  <div>
                    <div className="font-medium text-neutral-900">{opt.label}</div>
                    <div className="text-sm text-neutral-600">{opt.description}</div>
                  </div>
                </label>
              ))}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-neutral-700 mb-1">Display name (publicly shown)</label>
              <select
                className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
                value={primarySynonymId ?? ''}
                onChange={e => setPrimarySynonymId(e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">Use registered cultivar name</option>
                {synonyms.map(s => (
                  <option key={s.id} value={s.id}>{s.synonym} ({s.type.replace('_', ' ')})</option>
                ))}
              </select>
              <p className="text-xs text-neutral-500 mt-1">
                Choose a synonym to display in place of the registered name (useful while a PBR application is in progress).
              </p>
            </div>

            <div className="flex items-center gap-3">
              <Button onClick={handleSaveReleaseState} disabled={savingReleaseState} variant="primary">
                {savingReleaseState && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Save release settings
              </Button>
              {releaseStateSaved && <span className="text-sm text-success-700">Saved</span>}
              {releaseStateError && <span className="text-sm text-error-700">{releaseStateError}</span>}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ---- Per-region releases ---- */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Globe2 className="h-5 w-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-neutral-900">Per-region release</h2>
          </div>
          <p className="text-sm text-neutral-600 mb-4">
            Region licensors control whether this cultivar is released to growers in their region, and which name is shown publicly there. Region codes use ISO 3166-2 (e.g. AU-VIC, US-CA).
          </p>

          {regionReleasesLoading ? (
            <div className="flex items-center gap-2 text-neutral-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
          ) : (
            <>
              {regionReleases.length > 0 ? (
                <table className="w-full text-sm mb-4">
                  <thead>
                    <tr className="text-left text-neutral-500 border-b border-neutral-200">
                      <th className="py-2 pr-3 font-medium">Region</th>
                      <th className="py-2 pr-3 font-medium">Public name</th>
                      <th className="py-2 pr-3 font-medium">Released to growers</th>
                      <th className="py-2 font-medium text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {regionReleases.map(r => (
                      <tr key={r.id} className="border-b border-neutral-100">
                        <td className="py-2 pr-3 font-mono">{r.region_code}</td>
                        <td className="py-2 pr-3">
                          <select
                            className="rounded-md border border-neutral-300 px-2 py-1 text-sm"
                            value={r.public_synonym_id ?? ''}
                            onChange={e => handleSetRegionPublicSynonym(r, e.target.value ? Number(e.target.value) : null)}
                          >
                            <option value="">Registered name</option>
                            {synonyms.map(s => (
                              <option key={s.id} value={s.id}>{s.synonym}</option>
                            ))}
                          </select>
                        </td>
                        <td className="py-2 pr-3">
                          <span className={r.released_to_growers ? 'text-success-700' : 'text-neutral-500'}>
                            {r.released_to_growers ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td className="py-2 text-right">
                          <Button variant="outline" size="sm" onClick={() => handleToggleRegionReleased(r)}>
                            {r.released_to_growers ? 'Unrelease' : 'Release'}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-sm text-neutral-500 mb-4">No regions configured yet.</p>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 items-end">
                <Input
                  label="Add region (ISO 3166-2)"
                  value={newRegionCode}
                  onChange={e => setNewRegionCode(e.target.value.toUpperCase())}
                  placeholder="AU-VIC"
                />
                <Button onClick={handleAddRegion} disabled={addingRegion || !newRegionCode.trim()} variant="primary">
                  {addingRegion ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                </Button>
              </div>
              {regionError && <p className="mt-3 text-sm text-error-700">{regionError}</p>}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default BreederControls;
